import { Axios, AxiosResponse } from 'axios';
import { Pageable } from './pageable';
import { FilterExpression } from './umbrella';

export interface ApiError {
  title: string;
  message: string;
  errorCode: string;
  timestamp: Date;
}

export interface Sort {
  key: string;
  descending?: boolean;
}

export interface IdPathParameters<T = number> {
  id: T;
}

export interface SearchOptions {
  page?: number;
  size?: number;
  sort?: Sort[];
  filter?: FilterExpression | ((expr: FilterExpression) => FilterExpression);
  distinct?: boolean;
  includeTotal?: boolean;
  rootId?: string;
  domainOnly?: boolean;
}

export interface RequestFactoryOptions {
  pattern?: string;
  permissionId?: string;
  useSubscriptionId?: boolean;
  returnLocation?: boolean;
}

export const resolvePathUrl = <P>(pattern: string, params: P) => {
  if (!params) {
    return pattern;
  }

  return Object.entries(params).reduce((result, [key, value]) => {
    return result.replace(`{${key}}`, `${value}`);
  }, pattern);
};

/**
 * A search operation of T and P is a function that retrieves via HTTP a
 * page of things of type T, given standard Umbrella search options and
 * optionally also custom search options that this search function accepts on
 * top of what's provided by Umbrella.
 *
 * @typeParam T The type of object returned by the search
 * @typeParam P The type of the custom search parameters that the function
 *              accepts. If the function accepts no custom parameters, this
 *              should be `unknown`.
 *
 * @param options The standard Umbrella search options to control selection of
 *                results, sorting, and paging
 * @param customUrlParams Custom URL request parameters (like ?a=b&c=d) that
 *                        this search function accepts on top of Umbrella
 *                        standard parameters
 * @param pathVariables Custom request path parameters
 */
export type SearchOperation<T, P = unknown> = (
  options: SearchOptions,
  customUrlParams?: URLSearchParams,
  pathVariables?: P
) => Promise<Pageable<T>>;

/**
 * An unpaged search operation of T and P is a function that retrieves via HTTP
 * a things of type T, given standard Umbrella search options and optionally
 * also custom search options that this search function accepts on top of what's
 * provided by Umbrella.
 *
 * Note that unlike the regular SearchOperation, this kind of operation does
 * not necessarily return JSON. You can use this type for API endpoints that
 * stream data as a string or raw octets.
 *
 * @typeParam T The type of object returned by the search
 * @typeParam P The type of the custom search parameters that the function
 *              accepts. If the function accepts no custom parameters, this
 *              should be `never`.
 *
 * @param options The standard Umbrella search options to control selection of
 *                results, sorting, and paging
 * @param customUrlParams Custom URL request parameters (like ?a=b&c=d) that
 *                        this search function accepts on top of Umbrella
 *                        standard parameters
 * @param pathVariables Custom request path parameters
 */
export type UnpagedSearchOperation<T, P = never> = (
  options: SearchOptions,
  customUrlParams?: URLSearchParams,
  pathVariables?: P
) => Promise<T>;

/**
 * A function that retrieves a single result object given a single object identifier parameter.
 *
 * @typeParam Out The type of object returned
 * @typeParam Id The type of the identifier
 */
export type FindOperation<Out, Id = number> = (
  params: IdPathParameters<Id> | Record<string, any>,
  queryParams?: URLSearchParams
) => Promise<Out>;

/**
 *  A function that performs an update or creation of an HTTP resource
 *
 * @typeParam In The type of data sent to the server for update or creation
 * @typeParam Out The type of data that comes back from the server after the update or creation is done
 * @typeParam Id The type of the identifier of the object to update
 */
export type SaveOperation<In, Out = In, Id = number> = (
  params: IdPathParameters<Id> | Record<string, any>,
  data: In,
  queryParams?: URLSearchParams
) => Promise<Out>;

/**
 * A function that performs a deletion of an HTTP resource
 *
 * @param pattern
 * @returns
 */
export type DeleteOperation<Out, Id = number> = (params: IdPathParameters<Id> | Record<string, any>) => Promise<Out>;

// Reusable permission check helper
export async function checkPermissionOrEmpty<T>(
  permissionId: string | undefined,
  useSubscriptionId: boolean,
  emptyResult: T
): Promise<T | undefined> {
  if (!permissionId) {
    return undefined;
  }
  // Dynamically import to avoid circular dependency
  // eslint-disable-next-line @nx/enforce-module-boundaries
  const { useCurrentUserStore } = await import('@infuse/iam/user/store');
  const currentUserStore = useCurrentUserStore();
  if (
    currentUserStore.enabled &&
    !currentUserStore.hasPermission(permissionId, useSubscriptionId ? currentUserStore.mainSubscriptionId : undefined)
  ) {
    return emptyResult;
  }
  return undefined;
}

export const searchRequestFactory: <T, P = unknown>(
  client: Axios,
  options?: RequestFactoryOptions
) => SearchOperation<T, P> = <T, P = unknown>(client: Axios, options: RequestFactoryOptions = {}) =>
  unpagedSearchRequestFactory<Pageable<T>, P>(client, options);

export const unpagedSearchRequestFactory: <T, P = never>(
  client: Axios,
  options?: RequestFactoryOptions
) => UnpagedSearchOperation<T, P> = <T, P = never>(client: Axios, options: RequestFactoryOptions = {}) => {
  return async (searchOptions: SearchOptions, customUrlParams?: URLSearchParams, pathVariables?: P): Promise<T> => {
    const { pattern = '', permissionId, useSubscriptionId = false } = options;
    const denied = await checkPermissionOrEmpty(permissionId, useSubscriptionId, {} as T);
    if (denied !== undefined) {
      return denied;
    }

    let filter: FilterExpression | undefined;

    if (searchOptions.filter) {
      if (typeof searchOptions.filter === 'function') {
        filter = searchOptions.filter(new FilterExpression());
      } else {
        filter = searchOptions.filter;
      }
    }

    const queryParams = new URLSearchParams();

    if (searchOptions.distinct) {
      queryParams.append('distinct', 'true');
    }

    if (searchOptions.page) {
      queryParams.append('page', searchOptions.page.toString());
    }

    queryParams.append('size', searchOptions?.size?.toString() ?? '20');

    searchOptions?.sort?.forEach((sort) => {
      queryParams.append('sort', `${String(sort.key)},${sort.descending ? 'desc' : 'asc'}`);
    });

    if (filter) {
      queryParams.append('filter', filter.toString());
    }

    // Only forward includeTotal when explicitly set. If omitted, the backend default applies.
    if (searchOptions.includeTotal !== undefined) {
      queryParams.append('includeTotal', searchOptions.includeTotal.toString());
    }

    if (searchOptions.rootId) {
      queryParams.append('rootId', searchOptions.rootId);
    }

    if (searchOptions.domainOnly) {
      queryParams.append('domainOnly', 'true');
    }

    if (customUrlParams) {
      customUrlParams.forEach((v, k) => queryParams.append(k, v));
    }

    const url = resolvePathUrl(pattern, pathVariables);

    const response = await client.get<T>(url, {
      params: queryParams
    });

    return response.data;
  };
};

export const getRequestFactory: <Out, Id = number>(
  client: Axios,
  options?: RequestFactoryOptions
) => FindOperation<Out, Id> = <Out, Id = number>(client: Axios, options: RequestFactoryOptions = {}) => {
  return async <P = IdPathParameters<Id>>(params: P, queryParams?: URLSearchParams): Promise<Out> => {
    const { pattern = '{id}', permissionId, useSubscriptionId = false } = options;
    const denied = await checkPermissionOrEmpty(permissionId, useSubscriptionId, {} as Out);
    const url = resolvePathUrl(pattern, params);
    if (denied !== undefined) {
      throw new Error(`Error performing HTTP 'get'. No permission [${permissionId}] exists for this call: [${url}]`);
    }

    const response = await client.get<Out>(url, { params: queryParams });
    return response.data;
  };
};

export const getSingleRequestFactory: <Out>(client: Axios, options?: RequestFactoryOptions) => () => Promise<Out> = <
  Out
>(
  client: Axios,
  options: RequestFactoryOptions = {}
) => {
  return async (): Promise<Out> => {
    const { pattern = '{id}', permissionId, useSubscriptionId = false } = options;
    const denied = await checkPermissionOrEmpty(permissionId, useSubscriptionId, {} as Out);
    const url = resolvePathUrl(pattern, undefined);
    if (denied !== undefined) {
      throw new Error(
        `Error performing HTTP 'get' single. No permission [${permissionId}] exists for this call: [${url}]`
      );
    }

    const response = await client.get<Out>(url);
    return response.data;
  };
};

export const putRequestFactory: <In, Out = In, Id = number>(
  client: Axios,
  options?: RequestFactoryOptions
) => SaveOperation<In, Out, Id> = <In, Out = In, Id = number>(client: Axios, options: RequestFactoryOptions = {}) => {
  return async <P = IdPathParameters<Id>>(params: P, data: In, queryParams?: URLSearchParams): Promise<Out> => {
    const { pattern = '{id}', permissionId, useSubscriptionId = false } = options;
    const denied = await checkPermissionOrEmpty(permissionId, useSubscriptionId, {} as Out);
    const url = resolvePathUrl(pattern, params);
    if (denied !== undefined) {
      throw new Error(`Error performing HTTP 'put'. No permission [${permissionId}] exists for this call: [${url}]`);
    }

    const response = await client.put<Out>(url, data, { params: queryParams });
    return response.data;
  };
};

export const postRequestFactory: <In, Out = In, Id = number>(
  client: Axios,
  options?: RequestFactoryOptions
) => SaveOperation<In, Out, Id> = <In, Out = In, Id = number>(client: Axios, options: RequestFactoryOptions = {}) => {
  return async <P = IdPathParameters<Id>>(params: P, data: In, queryParams?: URLSearchParams): Promise<Out> => {
    const { pattern = '{id}', permissionId, useSubscriptionId = false, returnLocation = false } = options;
    const denied = await checkPermissionOrEmpty(permissionId, useSubscriptionId, {} as Out);
    const url = resolvePathUrl(pattern, params);
    if (denied !== undefined) {
      throw new Error(`Error performing HTTP 'post'. No permission [${permissionId}] exists for this call: [${url}]`);
    }
    const response = await client.post<Out>(url, data, { params: queryParams });
    if (returnLocation) {
      const location = response.headers.location;
      if (!location) {
        throw new Error('No location header returned from upload');
      }
      return location as unknown as Out;
    }
    return response.data;
  };
};

export const postDownloadRequestFactory = <P = Record<string, any>>(
  client: Axios,
  options: RequestFactoryOptions = {}
) => {
  return async (params: P): Promise<AxiosResponse<Blob>> => {
    const { pattern = '{id}', permissionId, useSubscriptionId = false } = options;

    const denied = await checkPermissionOrEmpty(permissionId, useSubscriptionId, {});
    const url = resolvePathUrl(pattern, params);

    if (denied !== undefined) {
      throw new Error(
        `Error performing HTTP 'post-download'. No permission [${permissionId}] exists for this call: [${url}]`
      );
    }

    return client.post(url, undefined, {
      responseType: 'blob',
      headers: {
        Accept: 'application/octet-stream'
      }
    });
  };
};

export type DownloadOperation<P = Record<string, any>> = (
  params: P,
  queryParams?: URLSearchParams
) => Promise<AxiosResponse<Blob>>;

export const getDownloadRequestFactory = <P = Record<string, any>>(
  client: Axios,
  options: RequestFactoryOptions = {}
): DownloadOperation<P> => {
  return async (params: P, queryParams?: URLSearchParams): Promise<AxiosResponse<Blob>> => {
    const { pattern = '{id}', permissionId, useSubscriptionId = false } = options;

    const denied = await checkPermissionOrEmpty(permissionId, useSubscriptionId, {});
    const url = resolvePathUrl(pattern, params);

    if (denied !== undefined) {
      throw new Error(
        `Error performing HTTP 'get-download'. No permission [${permissionId}] exists for this call: [${url}]`
      );
    }

    return client.get(url, {
      responseType: 'blob',
      params: queryParams,
      headers: {
        Accept: 'application/octet-stream'
      }
    });
  };
};

export const deleteRequestFactory: <Out, Id = number>(
  client: Axios,
  options?: RequestFactoryOptions
) => DeleteOperation<Out, Id> = <Out, Id = number>(client: Axios, options: RequestFactoryOptions = {}) => {
  return async <P = IdPathParameters<Id>>(params: P): Promise<Out> => {
    const { pattern = '{id}', permissionId, useSubscriptionId = false } = options;
    const denied = await checkPermissionOrEmpty(permissionId, useSubscriptionId, {} as Out);
    const url = resolvePathUrl(pattern, params);
    if (denied !== undefined) {
      throw new Error(`Error performing HTTP 'delete'. No permission [${permissionId}] exists for this call: [${url}]`);
    }

    const response = await client.delete<Out>(url);
    return response.data;
  };
};
